// ============================================
// FICHASUCURSAL.JS — Detalle de sucursal con ubicación + contactos
// ============================================

import React, { useEffect, useState } from 'react';
import PedirDatosCard from './PedirDatosCard';
import KitProveedorCard from './KitProveedorCard';
import GarantiasLista from './GarantiasLista';
import {
  API_BASE_CLI, PP, COLOR_PRINCIPAL, COLOR_OSCURO, COLOR_FONDO, COLOR_GRIS,
  COLOR_GRIS_CLARO, COLOR_BORDE, COLOR_ADVERTENCIA,
  estiloInput, estiloBotonPrimario, estiloBotonSecundario, estiloTarjeta,
  CabeceraFicha, faltantesSucursal,
} from './helpersClientes';
import { authHeaders } from '../utils/storage';
import SelectorMapa from './SelectorMapa';
import ContactosAlcance from './ContactosAlcance';
import BotonArchivarCliente from './BotonArchivarCliente';
import SelloAutoria from './SelloAutoria';

const COLOR_ERROR = '#e05c5c';

const CAMPOS = [
  { k: 'nombre_interno',   label: 'Nombre interno', requerido: true },
  { k: 'telefono',         label: 'Teléfono' },
  { k: 'direccion',        label: 'Dirección', multiline: true },
  { k: 'colonia',          label: 'Colonia' },
  { k: 'ciudad',           label: 'Ciudad' },
  { k: 'estado_republica', label: 'Estado' },
  { k: 'codigo_postal',    label: 'Código postal' },
  { k: 'notas_acceso',     label: 'Notas de acceso', multiline: true },
];

export default function FichaSucursal({ id, onVolver, esCelular }) {
  const [suc, setSuc] = useState(null);
  const [form, setForm] = useState({});
  const [editando, setEditando] = useState(false);
  const [modalMapa, setModalMapa] = useState(false);
  const [modalVincular, setModalVincular] = useState(false);
  const [modalGerente, setModalGerente] = useState(false);
  const [historialGerentes, setHistorialGerentes] = useState([]);
  const [countGerentes, setCountGerentes] = useState(0);
  const [cargando, setCargando] = useState(true);

  const cargar = async () => {
    setCargando(true);
    try {
      const [suR, hR, cR] = await Promise.all([
        fetch(`${API_BASE_CLI}/api/clientes/sucursales/${id}`, { headers: authHeaders() }).then(x => x.json()),
        fetch(`${API_BASE_CLI}/api/clientes/sucursales/${id}/historial-gerentes`, { headers: authHeaders() }).then(x => x.json()),
        fetch(`${API_BASE_CLI}/api/clientes/sucursales/${id}/count-gerentes-activos`, { headers: authHeaders() }).then(x => x.json()),
      ]);
      if (suR.ok) {
        setSuc(suR.sucursal);
        setForm(suR.sucursal);
      }
      if (hR.ok) setHistorialGerentes(hR.historial || []);
      if (cR.ok) setCountGerentes(cR.count || 0);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargar(); }, [id]);

  const guardar = async () => {
    const r = await fetch(`${API_BASE_CLI}/api/clientes/sucursales/${id}`, {
      method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }).then(x => x.json());
    if (r.ok) setSuc(r.sucursal);
    setEditando(false);
    cargar();
  };

  const guardarCoordenadas = async (payload) => {
    // payload: { latitud, longitud, direccion?, colonia?, ciudad?, estado?, codigo_postal? }
    const body = {
      latitud: payload.latitud,
      longitud: payload.longitud,
    };
    // Solo sobreescribimos si el selector envió el campo (= el usuario aceptó autocompletar)
    if (payload.direccion !== undefined) body.direccion = payload.direccion;
    if (payload.colonia !== undefined) body.colonia = payload.colonia;
    if (payload.ciudad !== undefined) body.ciudad = payload.ciudad;
    if (payload.estado !== undefined) body.estado_republica = payload.estado;
    if (payload.codigo_postal !== undefined) body.codigo_postal = payload.codigo_postal;

    const r = await fetch(`${API_BASE_CLI}/api/clientes/sucursales/${id}`, {
      method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    }).then(x => x.json());
    if (r.ok) { setSuc(r.sucursal); setForm(r.sucursal); }
    setModalMapa(false);
  };

  if (cargando || !suc) {
    return <div style={{ fontFamily: PP, padding: 40, textAlign: 'center', color: COLOR_GRIS_CLARO }}>Cargando sucursal…</div>;
  }

  const faltantes = faltantesSucursal(suc);
  const tieneCoord = suc.latitud != null && suc.longitud != null;

  return (
    <div style={{ fontFamily: PP, background: COLOR_FONDO, minHeight: '100%', color: COLOR_GRIS }}>
      <CabeceraFicha
        volver={onVolver}
        titulo={suc.nombre_interno}
        subtitulo={`Sucursal · ${suc.razon_social_nombre || ''}`}
        estado={suc.estado}
        acciones={
          editando
            ? (
              <>
                <button style={estiloBotonSecundario} onClick={() => { setEditando(false); setForm(suc); }}>Cancelar</button>
                <button style={{ ...estiloBotonPrimario, marginLeft: 8 }} onClick={guardar}>Guardar</button>
              </>
            )
            : <button style={estiloBotonSecundario} onClick={() => setEditando(true)}>Editar</button>
        }
      />

      <div style={{ padding: esCelular ? 16 : 24, display: 'grid', gap: 16 }}>
        {/* Banner huérfana */}
        {suc.falta_razon_social && (
          <div style={{
            background: '#fde8e8', border: `1px solid ${COLOR_ERROR}`, borderRadius: 10,
            padding: 14, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_ERROR }}>
                ⚠️ Esta sucursal no está vinculada a una razón social
              </div>
              <div style={{ fontSize: 12, color: '#8e3838', marginTop: 4 }}>
                No podrá facturarse hasta asignarla.
                {suc.grupo_nombre && <span> Actualmente bajo grupo (cliente): <strong>{suc.grupo_nombre}</strong></span>}
              </div>
            </div>
            <button
              onClick={() => setModalVincular(true)}
              style={{
                background: COLOR_ERROR, color: '#fff', border: 'none',
                padding: '10px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                cursor: 'pointer', fontFamily: PP,
              }}
            >
              Vincular a razón social
            </button>
          </div>
        )}

        {/* Ubicación */}
        <div style={estiloTarjeta}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 14 }}>Ubicación</div>

          <div style={{ display: 'grid', gridTemplateColumns: esCelular ? '1fr' : '1fr 1fr', gap: 14 }}>
            {CAMPOS.map(({ k, label, multiline, requerido }) => (
              <div key={k} style={{ gridColumn: multiline ? (esCelular ? 'auto' : '1 / -1') : 'auto' }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLOR_GRIS_CLARO, display: 'flex', alignItems: 'center', gap: 6 }}>
                  {label}
                  {!requerido && !suc[k] && !editando && <span title="Pendiente" style={{ color: COLOR_ADVERTENCIA }}>⚠️</span>}
                </label>
                {editando ? (
                  multiline ? (
                    <textarea style={{ ...estiloInput, minHeight: 60, resize: 'vertical' }} value={form[k] || ''} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} />
                  ) : (
                    <input style={estiloInput} value={form[k] || ''} onChange={(e) => setForm(f => ({ ...f, [k]: e.target.value }))} />
                  )
                ) : (
                  <div style={{ fontSize: 13, color: suc[k] ? COLOR_GRIS : COLOR_GRIS_CLARO, marginTop: 4, whiteSpace: 'pre-wrap' }}>
                    {suc[k] || '— Sin especificar —'}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Bloque coordenadas */}
          <div style={{
            marginTop: 14, padding: 14, background: COLOR_FONDO, borderRadius: 10,
            display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLOR_OSCURO }}>Coordenadas GPS</div>
              {tieneCoord ? (
                <div style={{ fontSize: 12, color: COLOR_GRIS, marginTop: 4 }}>
                  {Number(suc.latitud).toFixed(6)}, {Number(suc.longitud).toFixed(6)}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4 }}>Sin coordenadas registradas.</div>
              )}
            </div>
            {tieneCoord && (
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${suc.latitud},${suc.longitud}`}
                target="_blank" rel="noreferrer"
                style={{ ...estiloBotonSecundario, textDecoration: 'none', display: 'inline-block' }}
              >
                🗺️ Abrir en Google Maps
              </a>
            )}
            <button style={estiloBotonPrimario} onClick={() => setModalMapa(true)}>
              {tieneCoord ? 'Editar ubicación' : 'Capturar ubicación'}
            </button>
          </div>

          {faltantes.length > 0 && (
            <div style={{
              marginTop: 14, padding: 10, background: '#fff4e5', borderRadius: 8,
              fontSize: 12, color: '#a86b1e',
            }}>
              ⚠️ Datos pendientes: <strong>{faltantes.join(', ')}</strong>
            </div>
          )}
        </div>

        {/* Radio de seguridad */}
        <RadioSeguridad
          sucursalId={id}
          valorActual={suc.radio_seguridad_metros}
          onGuardado={(nuevo) => setSuc(s => ({ ...s, radio_seguridad_metros: nuevo }))}
        />

        {/* Banner preventivo: múltiples gerentes activos (dato autoritativo del backend) */}
        {countGerentes > 1 && (
          <div style={{
            background: '#fff4e5', border: `1px solid #f0d9b8`, borderRadius: 10,
            padding: 14, fontSize: 12, color: '#a86b1e', lineHeight: 1.5,
          }}>
            ⚠️ Esta sucursal tiene <strong>{countGerentes} gerentes activos</strong> simultáneamente.
            Archiva los duplicados para mantener consistencia.
          </div>
        )}

        {/* Gerente asignado */}
        <GerenteAsignado
          historial={historialGerentes}
          onCambiar={() => setModalGerente(true)}
        />

        {/* Contactos asignados */}
        <ContactosAlcance
          alcance_tipo="sucursal"
          alcance_id={id}
          titulo="Contactos de la sucursal"
          tiposPermitidos={['encargado_sucursal', 'gerente', 'administrador']}
        />

        {/* GARANTIAS (2026-09-07): certificados de este cliente, registro oficial. */}
        <div style={estiloTarjeta}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>Garantías</div>
          <GarantiasLista filtro={{ sucursal_id: id }} compacto />
        </div>
        {/* KIT DE PROVEEDOR (2026-09-08): lo que Beep entrega para darse de alta con este cliente. */}
        <KitProveedorCard cliente={{ sucursal_id: id, razon_social_id: suc.razon_social_id, grupo_id: suc.grupo_id, nombre: suc.nombre_interno }} />
        {/* PORTAL DEL CLIENTE (2026-09-08): pedirle sus datos con un enlace temporal. */}
        <PedirDatosCard cliente={{ sucursal_id: id, razon_social_id: suc.razon_social_id, grupo_id: suc.grupo_id, nombre: suc.nombre_interno }} />

        {/* Inventario (placeholder) */}
        <div style={estiloTarjeta}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>Inventario instalado</div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Se integrará en FASE 2 con equipos instalados.</div>
        </div>

        {/* Historial (placeholder) */}
        <div style={estiloTarjeta}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 10 }}>Historial de trabajos</div>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Se integrará en FASE 2 con reportes de servicio.</div>
        </div>

        {/* Historial de gerentes */}
        {historialGerentes.length > 0 && (
          <div style={estiloTarjeta}>
            <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 12 }}>
              📜 Historial de gerentes
            </div>
            <div style={{ display: 'grid', gap: 6 }}>
              {historialGerentes.slice(0, 5).map(h => {
                const activo = h.estado_rol === 'activo_confirmado';
                return (
                  <div key={h.id} style={{
                    display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
                    color: activo ? COLOR_OSCURO : COLOR_GRIS_CLARO,
                    padding: '6px 0',
                  }}>
                    <span style={{ fontSize: 10 }}>{activo ? '🟢' : '⚪'}</span>
                    <span style={{ fontWeight: activo ? 600 : 400 }}>{h.nombre_completo}</span>
                    <span style={{ color: COLOR_GRIS_CLARO }}>
                      ({formatRango(h.fecha_alta, h.fecha_baja, activo)})
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {modalMapa && (
        <SelectorMapa
          latitudInicial={suc.latitud}
          longitudInicial={suc.longitud}
          onGuardar={guardarCoordenadas}
          onCancelar={() => setModalMapa(false)}
          titulo={`📍 Ubicación de ${suc.nombre_interno}`}
        />
      )}
      {modalVincular && (
        <ModalVincularRazon
          sucursal={suc}
          onCerrar={() => setModalVincular(false)}
          onVinculado={() => { setModalVincular(false); cargar(); }}
        />
      )}
      {modalGerente && (
        <ModalCambiarGerente
          sucursalId={id}
          sucursalNombre={suc.nombre_interno}
          onCerrar={() => setModalGerente(false)}
          onGuardado={() => { setModalGerente(false); cargar(); }}
        />
      )}

      {/* AUTORIA-CLIENTES (2026-09-18): quien lo creo y quien lo toco por ultima vez.
          Lo anterior a la migracion 087 sale como "desconocido": no se le atribuye a nadie. */}
      <div style={{ padding: esCelular ? '0 16px' : '0 24px' }}>
        <SelloAutoria entidad={suc} />
      </div>

      {/* Acciones admin discretas — solo Dirección puede archivar */}
      <div style={{ padding: esCelular ? '20px 16px 32px' : '24px 24px 40px', display: 'flex', justifyContent: 'flex-end' }}>
        <BotonArchivarCliente
          tipo="sucursal" id={suc.id} entidad={suc}
          onArchivado={onVolver}
        />
      </div>
    </div>
  );
}

// ============================================
// Sección: gerente asignado + modal + helpers
// ============================================

function GerenteAsignado({ historial, onCambiar }) {
  const activo = historial.find(h => h.estado_rol === 'activo_confirmado');
  return (
    <div style={estiloTarjeta}>
      <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 14 }}>
        🏢 Gerente asignado
      </div>
      {activo ? (
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: COLOR_OSCURO }}>
              {activo.nombre_completo}
              {activo.puesto && <span style={{ color: COLOR_GRIS_CLARO, fontWeight: 400 }}> · {activo.puesto}</span>}
            </div>
            <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
              desde {formatFecha(activo.fecha_alta)}
            </div>
            {activo.celular && (
              <div style={{ fontSize: 12, color: COLOR_GRIS, marginTop: 6 }}>📱 {activo.celular}</div>
            )}
          </div>
          <button style={estiloBotonSecundario} onClick={onCambiar}>Cambiar gerente</button>
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Sin gerente asignado</div>
          <button style={estiloBotonPrimario} onClick={onCambiar}>+ Asignar gerente</button>
        </div>
      )}
    </div>
  );
}

function ModalCambiarGerente({ sucursalId, sucursalNombre, onCerrar, onGuardado }) {
  const [modo, setModo] = useState('existente');
  const [personas, setPersonas] = useState([]);
  const [personaId, setPersonaId] = useState('');
  const [nombre, setNombre] = useState('');
  const [celular, setCelular] = useState('');
  const [puesto, setPuesto] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (modo === 'existente' && personas.length === 0) {
      fetch(`${API_BASE_CLI}/api/clientes/personas`, { headers: authHeaders() }).then(x => x.json())
        .then(j => setPersonas(j.personas || []));
    }
  }, [modo, personas.length]);

  const guardar = async () => {
    setGuardando(true); setError(null);
    try {
      const body = { puesto: puesto || null };
      if (modo === 'existente') {
        if (!personaId) throw new Error('Selecciona una persona');
        body.persona_id = personaId;
      } else {
        if (!nombre.trim()) throw new Error('Nombre requerido');
        body.nueva_persona = { nombre: nombre.trim(), celular: celular || null };
      }
      const resp = await fetch(`${API_BASE_CLI}/api/clientes/sucursales/${sucursalId}/cambiar-gerente`, {
        method: 'POST', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const r = await resp.json();
      if (resp.status === 409) {
        alert(`⚠️ ${r.error}`);
        return;
      }
      if (!r.ok) throw new Error(r.error || 'Error');
      onGuardado();
    } catch (e) {
      setError(e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onCerrar}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 460, fontFamily: PP,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 6 }}>
          Cambiar gerente
        </div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 14 }}>
          Sucursal: {sucursalNombre}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <button
            onClick={() => setModo('existente')}
            style={{
              ...estiloBotonSecundario, border: 'none',
              background: modo === 'existente' ? COLOR_PRINCIPAL : '#fff',
              color: modo === 'existente' ? '#fff' : COLOR_GRIS,
            }}
          >Persona existente</button>
          <button
            onClick={() => setModo('nueva')}
            style={{
              ...estiloBotonSecundario, border: 'none',
              background: modo === 'nueva' ? COLOR_PRINCIPAL : '#fff',
              color: modo === 'nueva' ? '#fff' : COLOR_GRIS,
            }}
          >Nueva persona</button>
        </div>

        {modo === 'existente' ? (
          <div>
            <label style={{ fontSize: 12, fontWeight: 600 }}>Persona *</label>
            <select style={estiloInput} value={personaId} onChange={(e) => setPersonaId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {personas.map(p => <option key={p.id} value={p.id}>{p.nombre_completo}</option>)}
            </select>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Nombre *</label>
              <input style={estiloInput} value={nombre} onChange={(e) => setNombre(e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600 }}>Celular</label>
              <input style={estiloInput} value={celular} onChange={(e) => setCelular(e.target.value)} />
            </div>
          </div>
        )}

        <div style={{ marginTop: 10 }}>
          <label style={{ fontSize: 12, fontWeight: 600 }}>Puesto</label>
          <input style={estiloInput} value={puesto} onChange={(e) => setPuesto(e.target.value)} placeholder="Ej: Gerente regional" />
        </div>

        {error && <div style={{ color: '#e05c5c', fontSize: 12, marginTop: 10 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={estiloBotonSecundario} onClick={onCerrar}>Cancelar</button>
          <button style={estiloBotonPrimario} onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatFecha(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-MX', { year: 'numeric', month: 'short' });
  } catch { return String(iso); }
}

function formatRango(desde, hasta, activo) {
  const d = formatFecha(desde);
  if (activo) return `${d} - presente`;
  return `${d} - ${formatFecha(hasta)}`;
}

function RadioSeguridad({ sucursalId, valorActual, onGuardado }) {
  const [valor, setValor] = useState(valorActual != null ? valorActual : 100);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => { setValor(valorActual != null ? valorActual : 100); }, [valorActual]);

  const cambiar = async (v) => {
    const nuevo = Number(v);
    setValor(nuevo);
    setGuardando(true);
    try {
      const r = await fetch(`${API_BASE_CLI}/api/clientes/sucursales/${sucursalId}`, {
        method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ radio_seguridad_metros: nuevo }),
      }).then(x => x.json());
      if (r.ok) onGuardado(nuevo);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={estiloTarjeta}>
      <div style={{ fontSize: 14, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 8 }}>
        🎯 Radio de seguridad
      </div>
      <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginBottom: 14, lineHeight: 1.5 }}>
        Distancia máxima desde la sucursal donde el técnico puede iniciar el servicio.
      </div>
      <select
        value={valor}
        onChange={(e) => cambiar(e.target.value)}
        disabled={guardando}
        style={{ ...estiloInput, maxWidth: 180 }}
      >
        <option value={50}>50 m</option>
        <option value={100}>100 m</option>
        <option value={200}>200 m</option>
        <option value={500}>500 m</option>
        <option value={1000}>1000 m (1 km)</option>
      </select>
      <div style={{
        marginTop: 12, padding: 10, background: '#eaf2fb', borderRadius: 8,
        fontSize: 11, color: COLOR_OSCURO, lineHeight: 1.5,
      }}>
        ℹ️ Se aplicará cuando el módulo de reportes esté disponible.
      </div>
      {guardando && <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 6 }}>Guardando…</div>}
    </div>
  );
}

function ModalVincularRazon({ sucursal, onCerrar, onVinculado }) {
  const [razones, setRazones] = useState([]);
  const [razonId, setRazonId] = useState('');
  const [filtro, setFiltro] = useState(sucursal.grupo_id ? 'grupo' : 'todas');
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_CLI}/api/clientes/razones-sociales`, { headers: authHeaders() }).then(x => x.json()).then(j => {
      setRazones(j.razones_sociales || []);
    });
  }, []);

  const lista = filtro === 'grupo' && sucursal.grupo_id
    ? razones.filter(rs => rs.grupo_id === sucursal.grupo_id)
    : razones;

  const vincular = async () => {
    if (!razonId) return;
    setGuardando(true);
    try {
      await fetch(`${API_BASE_CLI}/api/clientes/sucursales/${sucursal.id}`, {
        method: 'PATCH', headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ razon_social_id: razonId }),
      });
      onVinculado();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.45)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }} onClick={onCerrar}>
      <div style={{
        background: '#fff', borderRadius: 14, padding: 24, width: '100%', maxWidth: 460, fontFamily: PP,
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 16, fontWeight: 700, color: COLOR_OSCURO, marginBottom: 14 }}>
          Vincular a razón social
        </div>

        {sucursal.grupo_id && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button
              onClick={() => setFiltro('grupo')}
              style={{
                ...estiloBotonSecundario,
                background: filtro === 'grupo' ? COLOR_PRINCIPAL : '#fff',
                color: filtro === 'grupo' ? '#fff' : COLOR_GRIS, border: 'none',
              }}
            >Del grupo (cliente)</button>
            <button
              onClick={() => setFiltro('todas')}
              style={{
                ...estiloBotonSecundario,
                background: filtro === 'todas' ? COLOR_PRINCIPAL : '#fff',
                color: filtro === 'todas' ? '#fff' : COLOR_GRIS, border: 'none',
              }}
            >Todas</button>
          </div>
        )}

        <label style={{ fontSize: 12, fontWeight: 600 }}>Razón social *</label>
        <select style={estiloInput} value={razonId} onChange={(e) => setRazonId(e.target.value)}>
          <option value="">— Selecciona —</option>
          {lista.map(rs => <option key={rs.id} value={rs.id}>{rs.razon_social}</option>)}
        </select>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button style={estiloBotonSecundario} onClick={onCerrar}>Cancelar</button>
          <button style={estiloBotonPrimario} onClick={vincular} disabled={!razonId || guardando}>
            {guardando ? 'Vinculando…' : 'Vincular'}
          </button>
        </div>
      </div>
    </div>
  );
}
